import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, MessageSquare, Trash2, Settings, User, ChevronDown, LogOut, UserCog, Palette, Sun, Moon, Menu, FileText, Sparkles, RefreshCw, Search, Edit2, Check, X, Brain, Folder, GripVertical, Globe, StickyNote, Eye, ExternalLink, HelpCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useAppearance } from '../contexts/AppearanceContext';
import { deleteConversation, updateConversation } from '../services/conversationApi';
import { getContextSpace, addUrlResource, addNoteResource, removeResourceFromContextSpace } from '../services/contextSpacesApi';
import ConfirmationModal from './ConfirmationModal';
import NewFeatureTag from './NewFeatureTag';
import { useNewFeatures } from '../contexts/NewFeaturesContext';
import { SeasonalBadge, SeasonalCornerDecoration } from './seasonal/SeasonalDecorations';

const Sidebar = ({ conversations, currentConversationId, onSelectConversation, onNewConversation, collapsed, onToggleCollapse, onOpenSettings, onDeleteConversation, onOpenFiles, onOpenApps, onUpdateConversation, onOpenMemory, onSelectContextSpace, onViewResource, selectedContextSpaceId: propSelectedContextSpaceId, onRefreshConversations, onShowContextSpaces, onStartGuidedTour, onLoadMoreConversations, hasMoreConversations }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState(null);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [dateGroupToDelete, setDateGroupToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingConversationId, setEditingConversationId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  // Context space resources state
  const [contextSpaceDetails, setContextSpaceDetails] = useState(null);
  const [showAddResourceModal, setShowAddResourceModal] = useState(false);
  const [newResourceType, setNewResourceType] = useState('URL');
  const [newResourceTitle, setNewResourceTitle] = useState('');
  const [newResourceDescription, setNewResourceDescription] = useState('');
  const [newResourceUrl, setNewResourceUrl] = useState('');
  const [newResourceContent, setNewResourceContent] = useState('');
  const [addingResource, setAddingResource] = useState(false);
  // Always show context spaces - no longer need toggle
  const showContextSpaces = true;
  // Use the selectedContextSpaceId from props instead of local state
  const selectedContextSpaceId = propSelectedContextSpaceId;
  const { user, logout } = useAuth();
  const { canViewSettings, isAdmin } = usePermissions();
  const { toggleAppearanceMode, appearanceMode, isSuppleMode, isSuppleLight, isSuppleDark } = useAppearance();
  const { markFeatureAsUsed, resetNewFeatures } = useNewFeatures();
  const userMenuRef = useRef(null);
  const searchInputRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const navigate = useNavigate();
  const formatDate = (date) => {
    const now = new Date();
    const messageDate = new Date(date);
    
    if (messageDate.toDateString() === now.toDateString()) {
      return 'Today';
    }
    
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (messageDate.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    
    return messageDate.toLocaleDateString();
  };

  // Filter conversations based on search query
  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      conv.title.toLowerCase().includes(query) ||
      conv.messages?.some(msg => 
        msg.content?.toLowerCase().includes(query)
      )
    );
  });

  const groupConversationsByDate = (conversations) => {
    const groups = {};
    
    conversations.forEach(conv => {
      const dateKey = formatDate(conv.createdAt);
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(conv);
    });
    
    return groups;
  };

  // Handle conversation title editing
  const handleEditStart = (conversation) => {
    setEditingConversationId(conversation.id);
    setEditingTitle(conversation.title);
  };

  const handleEditSave = async (conversationId) => {
    if (!editingTitle.trim()) {
      setEditingConversationId(null);
      setEditingTitle('');
      return;
    }

    try {
      await updateConversation(conversationId, { title: editingTitle.trim() });
      
      // Also call the parent's update function if provided to update local state
      if (onUpdateConversation) {
        await onUpdateConversation(conversationId, { title: editingTitle.trim() });
      }
      
      setEditingConversationId(null);
      setEditingTitle('');
    } catch (error) {
      console.error('Failed to update conversation title:', error);
      // Keep editing mode on error
    }
  };

  const handleEditCancel = () => {
    setEditingConversationId(null);
    setEditingTitle('');
  };

  const handleSearchIconClick = () => {
    // Expand the sidebar first
    onToggleCollapse();
    // Focus the search input after expansion
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100); // Small delay to ensure expansion animation completes
  };

  // Load context space details when selected
  useEffect(() => {
    const loadContextSpaceDetails = async () => {
      if (selectedContextSpaceId) {
        try {
          const details = await getContextSpace(selectedContextSpaceId);
          setContextSpaceDetails(details);
        } catch (error) {
          console.error('Failed to load context space details:', error);
          setContextSpaceDetails(null);
        }
      } else {
        setContextSpaceDetails(null);
      }
    };

    loadContextSpaceDetails();
  }, [selectedContextSpaceId]);

  // Handle Context Space selection
  const handleSelectContextSpace = (contextSpaceId) => {
    if (onSelectContextSpace) {
      onSelectContextSpace(contextSpaceId);
    }
  };

  // Resource management functions
  const handleAddResource = async () => {
    if (!selectedContextSpaceId || !newResourceTitle.trim()) return;

    try {
      setAddingResource(true);
      
      if (newResourceType === 'URL') {
        if (!newResourceUrl.trim()) return;
        await addUrlResource(selectedContextSpaceId, {
          title: newResourceTitle.trim(),
          description: newResourceDescription.trim(),
          url: newResourceUrl.trim()
        });
      } else if (newResourceType === 'NOTE') {
        if (!newResourceContent.trim()) return;
        await addNoteResource(selectedContextSpaceId, {
          title: newResourceTitle.trim(),
          description: newResourceDescription.trim(),
          content: newResourceContent.trim()
        });
      }

      // Reload context space details
      const updatedDetails = await getContextSpace(selectedContextSpaceId);
      setContextSpaceDetails(updatedDetails);
      
      // Reset form
      setNewResourceTitle('');
      setNewResourceDescription('');
      setNewResourceUrl('');
      setNewResourceContent('');
      setNewResourceType('URL');
      setShowAddResourceModal(false);
    } catch (error) {
      console.error('Failed to add resource:', error);
    } finally {
      setAddingResource(false);
    }
  };

  const handleDeleteResource = async (resourceId) => {
    if (!selectedContextSpaceId) return;

    try {
      await removeResourceFromContextSpace(selectedContextSpaceId, resourceId);
      
      // Reload context space details
      const updatedDetails = await getContextSpace(selectedContextSpaceId);
      setContextSpaceDetails(updatedDetails);
    } catch (error) {
      console.error('Failed to delete resource:', error);
    }
  };

  const [currentlyViewingResourceId, setCurrentlyViewingResourceId] = useState(null);

  const handleViewResource = (resource) => {
    // Mark resources section as used when viewing any resource
    markFeatureAsUsed('resources-section');
    
    // Toggle functionality: if clicking on the same resource that's currently being viewed, close it
    if (currentlyViewingResourceId === resource.id) {
      // Close the resource viewer
      onViewResource(null, null, null);
      setCurrentlyViewingResourceId(null);
    } else {
      // Pass the resource to the parent component for right panel display
      if (onViewResource) {
        onViewResource(selectedContextSpaceId, resource.id, resource);
        setCurrentlyViewingResourceId(resource.id);
      }
    }
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case 'URL':
        return <Globe className="w-4 h-4" />;
      case 'FILE':
        return <FileText className="w-4 h-4" />;
      case 'NOTE':
      case 'TEXT':
        return <StickyNote className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  // Get icon for conversation based on context space
  const getConversationIcon = (conversation) => {
    if (conversation.contextSpaces && conversation.contextSpaces.length > 0) {
      // Use the context space icon instead of MessageSquare
      const contextSpace = conversation.contextSpaces[0].contextSpace;
      return (
        <span className="text-sm" title={`In Context Space: ${contextSpace.name}`}>
          {contextSpace.icon}
        </span>
      );
    }
    return <MessageSquare className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
      isSuppleMode ? 'supple-text-muted' : 'text-claude-muted'
    }`} />;
  };


  // When collapsed, only show the latest 3 conversations, otherwise use filtered conversations
  const displayConversations = collapsed ? conversations.slice(0, 3) : filteredConversations;
  const groupedConversations = groupConversationsByDate(displayConversations);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Manual load more handler
  const handleLoadMoreClick = async () => {
    if (!hasMoreConversations || isLoadingMore || !onLoadMoreConversations) return;

    setIsLoadingMore(true);
    try {
      await onLoadMoreConversations();
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setShowUserMenu(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleDeleteConversation = async () => {
    if (!conversationToDelete) return;
    
    try {
      await deleteConversation(conversationToDelete.id);
      
      // If we're deleting the current conversation, select another one
      if (currentConversationId === conversationToDelete.id) {
        const remainingConversations = conversations.filter(c => c.id !== conversationToDelete.id);
        if (remainingConversations.length > 0) {
          onSelectConversation(remainingConversations[0].id);
        } else {
          onNewConversation();
        }
      }
      
      // Call the parent's delete handler
      onDeleteConversation(conversationToDelete.id);
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  };

  const handleBulkDeleteConversations = async () => {
    if (!dateGroupToDelete) return;
    
    try {
      // Delete all conversations for this date group
      const conversationsToDelete = dateGroupToDelete.conversations;
      
      for (const conv of conversationsToDelete) {
        await deleteConversation(conv.id);
        onDeleteConversation(conv.id);
      }
      
      // If current conversation was deleted, create a new one
      const deletedIds = conversationsToDelete.map(c => c.id);
      if (deletedIds.includes(currentConversationId)) {
        const remainingConversations = conversations.filter(c => !deletedIds.includes(c.id));
        if (remainingConversations.length > 0) {
          onSelectConversation(remainingConversations[0].id);
        } else {
          onNewConversation();
        }
      }
    } catch (error) {
      console.error('Failed to delete conversations:', error);
    }
  };

  return (
    <>
      {/* Mobile overlay backdrop */}
      {!collapsed && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={onToggleCollapse}
        />
      )}
      
      {/* Sidebar */}
      <div className={`
        ${collapsed ? 'w-16' : 'w-80'} 
        md:flex appearance-${appearanceMode} 
        ${isSuppleMode ? 'supple-card-frosted ' : 'bg-claude-sidebar border-r border-claude-border'} 
        flex flex-col transition-all duration-300 flex-shrink-0 
        ${collapsed ? 'hidden md:flex h-full' : 'fixed md:relative z-50 md:z-auto h-screen md:h-full overflow-hidden'}
      `}>
      {/* Header Section with Toggle Button */}
      <div className={`p-4 ${isSuppleMode ? '' : ' border-claude-border'}`}>
        {!collapsed ? (
          <div className="space-y-3">
            {/* Supple Chat Title with Hamburger/Collapse Button */}
            <div className="flex items-center justify-between">
              <h1 className={`text-lg font-bold ${isSuppleMode ? 'supple-text-primary' : 'text-claude-text'}`}>
                Supple Chat
              </h1>
              <button
                onClick={onToggleCollapse}
                className={`p-2 rounded-lg transition-colors ${
                  isSuppleMode 
                    ? 'hover:supple-card-glass-hover supple-text-muted' 
                    : 'hover:bg-gray-200'
                }`}
                title="Collapse sidebar"
              >
                <Menu className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        ) : (
          /* Collapsed view - hamburger menu and search centered */
          <div className="flex flex-col items-center space-y-2">
            <button
              onClick={onToggleCollapse}
              className={`p-3 rounded-lg transition-colors ${
                isSuppleMode 
                  ? 'hover:supple-card-glass-hover supple-text-muted' 
                  : 'hover:bg-gray-200'
              }`}
              title="Expand sidebar"
            >
              <Menu className="w-5 h-5 text-gray-600" />
            </button>
            {/* New Chat button - moved to be after menu and before search */}
            <button
              onClick={onNewConversation}
              className={`p-3 rounded-lg transition-colors ${
                isSuppleMode 
                  ? 'supple-btn-gradient-blue supple-shadow-md hover:supple-btn-gradient-blue-hover' 
                  : 'bg-claude-accent text-white hover:bg-opacity-90'
              }`}
              title="New Chat"
            >
              <Plus className="w-5 h-5" />
            </button>
            <button
              onClick={handleSearchIconClick}
              className={`p-3 rounded-lg transition-colors ${
                isSuppleMode 
                  ? 'hover:supple-card-glass-hover supple-text-muted' 
                  : 'hover:bg-gray-200'
              }`}
              title="Search conversations"
            >
              <Search className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={handleSearchIconClick}
              className={`p-3 rounded-lg transition-colors ${
                isSuppleMode 
                  ? 'hover:supple-card-glass-hover supple-text-muted' 
                  : 'hover:bg-gray-200'
              }`}
              title="Search conversations"
            >
              <MessageSquare className="w-5 h-5 text-gray-600" />
            </button>
            {/* Context Spaces button for collapsed sidebar */}
            <button
              onClick={() => {
                markFeatureAsUsed('context-spaces');
                onShowContextSpaces && onShowContextSpaces();
              }}
              className={`p-3 rounded-lg transition-colors relative ${
                isSuppleMode 
                  ? 'hover:supple-card-glass-hover supple-text-muted' 
                  : 'hover:bg-gray-200'
              }`}
              title="Context Spaces"
              data-tour="context-spaces"
            >
              <Folder className="w-5 h-5 text-gray-600" />
              <div className="absolute -top-1 -right-1">
                <NewFeatureTag featureId="context-spaces" size="xs" />
              </div>
            </button>
          </div>
        )}
        
      </div>

      {/* New Chat Section - Only for expanded view */}
      {!collapsed && (
        <div className={`px-4 pb-4 ${isSuppleMode ? '' : ' '}`}>
          <button
            onClick={onNewConversation}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              isSuppleMode 
                ? 'supple-btn-gradient-blue supple-shadow-md hover:supple-btn-gradient-blue-hover' 
                : 'bg-claude-accent text-white hover:bg-opacity-90'
            }`}
          >
            <Plus className="w-5 h-5" />
            <span className="font-medium">New Chat</span>
          </button>
        </div>
      )}

      {/* Search Section */}
      {!collapsed && (
        <div className="px-4 pb-4">
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${
              isSuppleMode ? 'supple-text-muted' : 'text-gray-400'
            }`} />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-lg border transition-colors ${
                isSuppleMode 
                  ? 'supple-card-glass supple-border-subtle supple-text-primary placeholder-gray-400' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              }`}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded transition-colors ${
                  isSuppleMode ? 'hover:supple-card-glass-hover' : 'hover:bg-gray-100'
                }`}
              >
                <X className="w-3 h-3 text-gray-400" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Context Spaces Button - Below search box */}
      {!collapsed && (
        <div className="px-4 py-2">
          <button
            onClick={() => {
              markFeatureAsUsed('context-spaces');
              onShowContextSpaces && onShowContextSpaces();
            }}
            className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors relative ${
              isSuppleMode
                ? 'hover:supple-card-glass-hover supple-text-primary'
                : 'hover:bg-gray-50 text-gray-900'
            }`}
            data-tour="context-spaces"
          >
            <Folder className={`w-5 h-5 ${
              isSuppleMode ? 'supple-text-primary' : 'text-gray-600'
            }`} />
            <span className="font-medium">Context Spaces</span>
            <div className="absolute top-1 right-1">
              <NewFeatureTag featureId="context-spaces" size="xs" />
            </div>
          </button>
        </div>
      )}

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto min-h-0 mobile-sidebar-scroll scrollbar-hide">
        {/* Context Spaces - Always visible */}

        {!collapsed ? (
          <>
            {/* Context Space Header */}
            {selectedContextSpaceId && (
              <div className="p-4 border-b border-gray-200">
                <div className={`flex items-center justify-between mb-2`}>
                  <h3 className={`text-sm font-semibold ${
                    isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
                  }`}>
                    {contextSpaceDetails?.name || 'Context Space'}
                  </h3>
                  <button
                    onClick={() => handleSelectContextSpace(null)}
                    className={`text-xs px-2 py-1 rounded transition-colors ${
                      isSuppleMode 
                        ? 'supple-text-muted hover:supple-text-primary' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Exit Space
                  </button>
                </div>
                
                {/* Context Space Resources */}
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-3" data-tour="resources-section">
                    <div className="flex items-center gap-2">
                      <h4 className={`text-xs font-semibold uppercase tracking-wider ${
                        isSuppleMode ? 'supple-text-muted' : 'text-gray-600'
                      }`}>
                        Resources ({contextSpaceDetails?.resources?.length || 0})
                      </h4>
                      <NewFeatureTag featureId="resources-section" size="xs" />
                    </div>
                    <button
                      onClick={() => {
                        markFeatureAsUsed('add-resource');
                        setShowAddResourceModal(true);
                      }}
                      className={`p-1 rounded transition-colors ${
                        isSuppleMode 
                          ? 'supple-text-muted hover:supple-text-primary hover:supple-card-glass-hover' 
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                      title="Add Resource"
                      data-tour="add-resource"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  
                  {contextSpaceDetails?.resources && contextSpaceDetails.resources.length > 0 ? (
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {contextSpaceDetails.resources.map((resource) => (
                        <div
                          key={resource.id}
                          className={`flex items-center gap-2 p-2 rounded transition-colors group cursor-pointer ${
                            isSuppleMode 
                              ? 'hover:supple-card-glass-hover' 
                              : 'hover:bg-gray-50'
                          }`}
                          onClick={() => handleViewResource(resource)}
                        >
                          <div className={`${
                            isSuppleMode ? 'supple-text-muted' : 'text-gray-500'
                          }`}>
                            {getResourceIcon(resource.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`text-sm font-medium truncate ${
                              isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
                            }`}>
                              {resource.title}
                            </div>
                            {resource.description && (
                              <div className={`text-xs truncate ${
                                isSuppleMode ? 'supple-text-muted' : 'text-gray-500'
                              }`}>
                                {resource.description}
                              </div>
                            )}
                          </div>
                          <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteResource(resource.id);
                              }}
                              className="p-1 rounded text-red-500 hover:text-red-700 transition-colors"
                              title="Delete Resource"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={`text-xs text-center py-4 ${
                      isSuppleMode ? 'supple-text-muted' : 'text-gray-500'
                    }`}>
                      No resources yet
                    </div>
                  )}
                </div>
                
                {/* Conversations Header */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className={`text-xs font-semibold uppercase tracking-wider ${
                    isSuppleMode ? 'supple-text-muted' : 'text-gray-600'
                  }`}>
                    Conversations
                  </h4>
                </div>
              </div>
            )}
            
            {Object.entries(groupedConversations).map(([dateGroup, convs]) => (
              <div key={dateGroup} className="p-4">
                <div className="flex items-center justify-between mb-3 group">
                  <h3 className={`text-xs font-semibold uppercase tracking-wider ${
                    isSuppleMode ? 'supple-text-muted' : 'text-claude-muted'
                  }`}>
                    {dateGroup}
                  </h3>
                  <button
                    onClick={() => {
                      setDateGroupToDelete({ dateGroup, conversations: convs });
                      setBulkDeleteModalOpen(true);
                    }}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded text-red-600 hover:text-red-700 transition-all"
                    title={`Delete all chats from ${dateGroup}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
                <div className="space-y-2">
                  {convs.map((conversation) => (
                    <div
                      key={conversation.id}
                      draggable={true}
                      onDragStart={(e) => {
                        e.dataTransfer.setData('application/json', JSON.stringify({
                          type: 'conversation',
                          conversation: conversation
                        }));
                        e.dataTransfer.effectAllowed = 'copy';
                      }}
                      onClick={() => onSelectConversation(conversation.id)}
                      className={`
                        w-full text-left p-3 rounded-lg transition-colors group relative cursor-grab active:cursor-grabbing
                        ${editingConversationId === conversation.id
                          ? isSuppleMode
                            ? 'supple-card-glass supple-border-gradient-purple supple-shadow-lg ring-2 ring-purple-200'
                            : 'bg-blue-50 border-2 border-blue-300 shadow-md ring-2 ring-blue-100'
                          : currentConversationId === conversation.id 
                            ? isSuppleMode 
                              ? 'supple-card-glass supple-border-gradient-blue supple-shadow-md' 
                              : 'bg-white border border-claude-border shadow-sm'
                            : isSuppleMode
                              ? 'hover:supple-card-glass-hover'
                              : 'hover:bg-white hover:bg-opacity-50'
                        }
                      `}
                      title={`Drag to add to context space: ${conversation.title}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex items-center gap-1">
                          <GripVertical className={`w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity ${
                            isSuppleMode ? 'supple-text-muted' : 'text-claude-muted'
                          }`} title="Drag to add to context space" />
                          {getConversationIcon(conversation)}
                        </div>
                        <div className="flex-1 min-w-0">
                          {editingConversationId === conversation.id ? (
                            <input
                              type="text"
                              value={editingTitle}
                              onChange={(e) => setEditingTitle(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleEditSave(conversation.id);
                                } else if (e.key === 'Escape') {
                                  handleEditCancel();
                                }
                              }}
                              onBlur={() => handleEditSave(conversation.id)}
                              className={`w-full text-sm font-medium px-2 py-1 rounded border-2 outline-none ${
                                isSuppleMode 
                                  ? 'bg-white/90 border-purple-300 text-gray-900 focus:border-purple-500 focus:ring-2 focus:ring-purple-200' 
                                  : 'bg-white border-blue-300 text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
                              }`}
                              autoFocus
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <div className="flex items-center gap-2">
                              <p className={`text-sm font-medium truncate ${
                                isSuppleMode ? 'supple-text-primary' : 'text-claude-text'
                              }`}>
                                {conversation.title}
                              </p>
                              {conversation.user && conversation.user.id !== user?.id && (
                                <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-medium flex-shrink-0">
                                  {conversation.user.picture ? (
                                    <img 
                                      src={conversation.user.picture} 
                                      alt={conversation.user.name} 
                                      className="w-3 h-3 rounded-full"
                                    />
                                  ) : (
                                    <div className="w-3 h-3 rounded-full bg-blue-600 flex items-center justify-center">
                                      <span className="text-white text-xs">
                                        {conversation.user.name?.charAt(0) || conversation.user.email?.charAt(0) || '?'}
                                      </span>
                                    </div>
                                  )}
                                  <span className="truncate max-w-16">
                                    {conversation.user.name || conversation.user.email}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                          {conversation.messages && conversation.messages.length > 0 && editingConversationId !== conversation.id && (
                            <p className={`text-xs mt-1 truncate ${
                              isSuppleMode ? 'supple-text-muted' : 'text-claude-muted'
                            }`}>
                              {conversation.messages[conversation.messages.length - 1].content}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Hover actions */}
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="flex items-center gap-1">
                          {editingConversationId === conversation.id ? (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditSave(conversation.id);
                                }}
                                className="p-1 hover:bg-green-100 rounded text-green-600 hover:text-green-700"
                                title="Save changes"
                              >
                                <Check className="w-3 h-3" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditCancel();
                                }}
                                className="p-1 hover:bg-gray-100 rounded text-gray-600 hover:text-gray-700"
                                title="Cancel editing"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditStart(conversation);
                                }}
                                className="p-1 hover:bg-blue-100 rounded text-blue-600 hover:text-blue-700"
                                title="Edit conversation title"
                              >
                                <Edit2 className="w-3 h-3" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConversationToDelete(conversation);
                                  setDeleteModalOpen(true);
                                }}
                                className="p-1 hover:bg-red-100 rounded text-red-600 hover:text-red-700"
                                title="Delete conversation"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Manual load more button */}
            {hasMoreConversations && conversations.length > 0 && (
              <div className="p-4 text-center">
                <button
                  onClick={handleLoadMoreClick}
                  disabled={isLoadingMore}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    isLoadingMore
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:scale-105'
                  } ${
                    isSuppleMode
                      ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  {isLoadingMore ? (
                    <span className="flex items-center gap-2">
                      <div className={`inline-block animate-spin rounded-full h-4 w-4 border-b-2 ${
                        isSuppleMode ? 'border-white' : 'border-gray-900'
                      }`}></div>
                      Loading...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <RefreshCw className="w-4 h-4" />
                      Load Previous Conversations
                    </span>
                  )}
                </button>
              </div>
            )}

            {/* Show when all conversations are loaded */}
            {!hasMoreConversations && conversations.length > 0 && (
              <div className="p-4 text-center">
                <p className={`text-xs ${
                  isSuppleMode ? 'supple-text-muted' : 'text-gray-500'
                }`}>All conversations loaded</p>
              </div>
            )}

            {conversations.length === 0 && (
              <div className="p-4 text-center">
                <MessageSquare className="w-12 h-12 text-claude-muted mx-auto mb-4 opacity-50" />
                <p className="text-claude-muted text-sm">No conversations yet</p>
                <p className="text-claude-muted text-xs mt-1">Start a new chat to begin</p>
              </div>
            )}
          </>
        ) : (
          <div className="p-2 space-y-2">
            {displayConversations.map((conversation) => (
              <button
                key={conversation.id}
                draggable={true}
                onDragStart={(e) => {
                  e.dataTransfer.setData('application/json', JSON.stringify({
                    type: 'conversation',
                    conversation: conversation
                  }));
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                onClick={() => onSelectConversation(conversation.id)}
                className={`
                  w-full p-3 rounded-lg transition-colors cursor-grab active:cursor-grabbing
                  ${currentConversationId === conversation.id 
                    ? 'bg-white border border-claude-border shadow-sm' 
                    : 'hover:bg-white hover:bg-opacity-50'
                  }
                `}
                title={`${conversation.title} - Drag to add to context space`}
              >
                <div className="flex justify-center">
                  {getConversationIcon(conversation)}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {!collapsed && (
        <div className={`p-4 space-y-3 flex-shrink-0 ${
          isSuppleMode ? 'supple-border-subtle border-t' : 'border-t border-claude-border'
        }`}>
          {/* Quick Actions Grid - Compact on Mobile */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            {/* Memory Button */}
            <button
              onClick={() => onOpenMemory && onOpenMemory()}
              className={`flex flex-col items-center p-2 rounded-lg transition-colors ${
                isSuppleMode 
                  ? 'supple-text-primary hover:supple-card-glass-hover' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              title="Memory"
            >
              <Brain className="w-4 h-4 mb-1" />
              <span className="text-xs font-medium">Memory</span>
            </button>

            {/* Files Button */}
            <button
              onClick={() => onOpenFiles && onOpenFiles()}
              className={`flex flex-col items-center p-2 rounded-lg transition-colors ${
                isSuppleMode 
                  ? 'supple-text-primary hover:supple-card-glass-hover' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              title="Files"
            >
              <FileText className="w-4 h-4 mb-1" />
              <span className="text-xs font-medium">Files</span>
            </button>

            {/* AI Apps Button - Admin only */}
            {isAdmin && (
              <button
                onClick={() => {
                  navigate('/apps');
                  onOpenApps && onOpenApps();
                }}
                className={`flex flex-col items-center p-2 rounded-lg transition-colors ${
                  isSuppleMode 
                    ? 'supple-text-primary hover:supple-card-glass-hover' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                title="AI Apps"
              >
                <Sparkles className="w-4 h-4 mb-1" />
                <span className="text-xs font-medium">Apps</span>
              </button>
            )}

          </div>
          
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {/* Appearance toggle */}
              <button
                onClick={toggleAppearanceMode}
                className={`p-2 rounded-lg transition-all duration-300 ${
                  appearanceMode === 'supple-light'
                    ? 'bg-gradient-to-r from-blue-400 to-blue-600 text-white shadow-lg transform hover:scale-105'
                    : appearanceMode === 'supple-dark'
                      ? 'bg-gradient-to-r from-purple-500 to-indigo-700 text-white shadow-lg transform hover:scale-105'
                      : 'text-gray-700 hover:bg-gray-100 bg-gray-50'
                }`}
                title={`Current: ${appearanceMode === 'supple-light' ? 'Supple Light' : appearanceMode === 'supple-dark' ? 'Supple Dark' : 'Plain'} - Click to cycle`}
              >
                {appearanceMode === 'supple-light' ? (
                  <Sun className="w-4 h-4" />
                ) : appearanceMode === 'supple-dark' ? (
                  <Moon className="w-4 h-4" />
                ) : (
                  <Palette className="w-4 h-4" />
                )}
              </button>
              
              {canViewSettings() && (
                <button
                  onClick={onOpenSettings}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                    isSuppleMode 
                      ? 'supple-text-primary hover:supple-card-glass-hover' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Settings className="w-5 h-5" />
                  <span className="font-medium">Settings</span>
                </button>
              )}
            </div>
            
            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                  isSuppleMode 
                    ? 'supple-text-primary hover:supple-card-glass-hover' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                title="User menu"
              >
                {user?.picture ? (
                  <img src={user.picture} alt={user.name || 'User'} className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isSuppleMode ? 'supple-btn-gradient-blue' : 'bg-claude-accent'
                  }`}>
                    <User className="w-5 h-5 text-white" />
                  </div>
                )}
                <ChevronDown className={`w-4 h-4 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
              </button>
              
              {showUserMenu && (
                <div className={`absolute bottom-full right-0 mb-2 w-64 rounded-lg py-2 ${
                  isSuppleMode 
                    ? 'supple-card-glass  supple-shadow-xl backdrop-blur-lg' 
                    : 'bg-white border border-gray-200 shadow-lg'
                }`}>
                  <div className={`px-4 py-2 ${
                    isSuppleMode ? 'supple-border-subtle border-b' : 'border-b border-gray-100'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isSuppleMode ? 'supple-btn-gradient-blue' : 'bg-claude-accent'
                      }`}>
                        {user?.picture ? (
                          <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                        ) : (
                          <User className="w-6 h-6 text-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${
                          isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
                        }`}>
                          {user?.name || 'User'}
                        </p>
                        <p className={`text-xs truncate ${
                          isSuppleMode ? 'supple-text-muted' : 'text-gray-500'
                        }`}>
                          {user?.email || 'Not signed in'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      // TODO: Implement user profile
                      console.log('Open user profile');
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                      isSuppleMode 
                        ? 'supple-text-primary hover:supple-card-glass-hover' 
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <UserCog className="w-4 h-4" />
                    <span>User Profile</span>
                  </button>
                  
                  <button
                    onClick={handleLogout}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                      isSuppleMode 
                        ? 'supple-text-primary hover:supple-card-glass-hover' 
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
          
          <div className="text-xs text-claude-muted text-center">
            <div className="flex items-center justify-center gap-2">
              <p>Supple Agentic Chat v2.2.0</p>
              <SeasonalBadge />
              <button
                onClick={() => window.location.reload()}
                className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                  isSuppleMode ? 'supple-text-muted hover:supple-text-primary' : ''
                }`}
                title="Check for updates"
              >
                <RefreshCw className="w-3 h-3" />
              </button>
              <button
                onClick={() => {
                  // Reset new feature tags when starting guided tour
                  resetNewFeatures();
                  onStartGuidedTour && onStartGuidedTour();
                }}
                className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                  isSuppleMode ? 'supple-text-muted hover:supple-text-primary' : ''
                }`}
                title="New Features Guide"
              >
                <HelpCircle className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Collapsed sidebar bottom section */}
      {collapsed && (
        <div className={`p-2 space-y-2 ${
          isSuppleMode ? 'supple-border-subtle border-t' : 'border-t border-claude-border'
        }`}>
          {/* Memory button */}
          <button
            onClick={() => onOpenMemory && onOpenMemory()}
            className={`w-full p-2 rounded-lg transition-colors ${
              isSuppleMode 
                ? 'supple-text-primary hover:supple-card-glass-hover' 
                : 'text-gray-700 hover:bg-gray-100'
            }`}
            title="Memory - Set your AI preferences"
          >
            <Brain className="w-4 h-4 mx-auto" />
          </button>

          {/* Files button */}
          <button
            onClick={() => onOpenFiles && onOpenFiles()}
            className={`w-full p-2 rounded-lg transition-colors ${
              isSuppleMode 
                ? 'supple-text-primary hover:supple-card-glass-hover' 
                : 'text-gray-700 hover:bg-gray-100'
            }`}
            title="Manage uploaded files"
          >
            <FileText className="w-4 h-4 mx-auto" />
          </button>
          
          {/* AI Apps button - Admin only */}
          {isAdmin && (
            <button
              onClick={() => {
                navigate('/apps');
                onOpenApps && onOpenApps();
              }}
              className={`w-full p-2 rounded-lg transition-colors ${
                isSuppleMode 
                  ? 'supple-text-primary hover:supple-card-glass-hover' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              title="AI Assistants"
            >
              <Sparkles className="w-4 h-4 mx-auto" />
            </button>
          )}

          
          {/* Appearance toggle */}
          <button
            onClick={toggleAppearanceMode}
            className={`w-full p-2 rounded-lg transition-all duration-300 ${
              appearanceMode === 'supple-light'
                ? 'bg-gradient-to-r from-blue-400 to-blue-600 text-white shadow-lg transform hover:scale-105'
                : appearanceMode === 'supple-dark'
                  ? 'bg-gradient-to-r from-purple-500 to-indigo-700 text-white shadow-lg transform hover:scale-105'
                  : 'text-gray-700 hover:bg-gray-100 bg-gray-50'
            }`}
            title={`Current: ${appearanceMode === 'supple-light' ? 'Supple Light' : appearanceMode === 'supple-dark' ? 'Supple Dark' : 'Plain'} - Click to cycle`}
          >
            {appearanceMode === 'supple-light' ? (
              <Sun className="w-4 h-4 mx-auto" />
            ) : appearanceMode === 'supple-dark' ? (
              <Moon className="w-4 h-4 mx-auto" />
            ) : (
              <Palette className="w-4 h-4 mx-auto" />
            )}
          </button>
          
          {canViewSettings() && (
            <button
              onClick={onOpenSettings}
              className={`w-full p-2 rounded-lg transition-colors ${
                isSuppleMode 
                  ? 'supple-text-primary hover:supple-card-glass-hover' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              title="Settings"
            >
              <Settings className="w-4 h-4 mx-auto" />
            </button>
          )}
          
          {/* User profile button - fixed size */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className={`w-full p-2 rounded-lg transition-colors flex items-center justify-center ${
                isSuppleMode 
                  ? 'supple-text-primary hover:supple-card-glass-hover' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              title="User menu"
            >
              {user?.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name || 'User'} 
                  className="w-8 h-8 rounded-full object-cover flex-shrink-0" 
                />
              ) : (
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isSuppleMode ? 'supple-btn-gradient-blue' : 'bg-claude-accent'
                }`}>
                  <User className="w-4 h-4 text-white" />
                </div>
              )}
            </button>
            
            {showUserMenu && (
              <div className={`absolute bottom-full left-0 mb-2 min-w-48 rounded-lg shadow-lg py-2 z-50 ${
                isSuppleMode 
                  ? 'supple-card-glass border' 
                  : 'bg-white border border-gray-200'
              }`}>
                <div className="px-4 py-2 border-b border-gray-200">
                  <p className={`font-medium text-sm ${
                    isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
                  }`}>
                    {user?.name || 'User'}
                  </p>
                  <p className={`text-xs ${
                    isSuppleMode ? 'supple-text-secondary' : 'text-gray-500'
                  }`}>
                    {user?.email}
                  </p>
                </div>
                
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    // Add user profile action here if needed
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                    isSuppleMode 
                      ? 'supple-text-primary hover:supple-card-glass-hover' 
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <UserCog className="w-4 h-4" />
                  <span>User Profile</span>
                </button>
                
                <button
                  onClick={handleLogout}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                    isSuppleMode 
                      ? 'supple-text-primary hover:supple-card-glass-hover' 
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setConversationToDelete(null);
        }}
        onConfirm={handleDeleteConversation}
        title="Delete Conversation"
        message={`Are you sure you want to delete "${conversationToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
      
      <ConfirmationModal
        isOpen={bulkDeleteModalOpen}
        onClose={() => {
          setBulkDeleteModalOpen(false);
          setDateGroupToDelete(null);
        }}
        onConfirm={handleBulkDeleteConversations}
        title="Delete All Conversations"
        message={`Are you sure you want to delete all ${dateGroupToDelete?.conversations?.length || 0} conversations from ${dateGroupToDelete?.dateGroup}? This action cannot be undone.`}
        confirmText="Delete All"
        cancelText="Cancel"
        type="danger"
      />
      
      {/* Add Resource Modal */}
      {showAddResourceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-md rounded-lg p-6 ${
            isSuppleMode 
              ? 'supple-card-glass backdrop-blur-lg' 
              : 'bg-white border border-gray-200'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold ${
                isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
              }`}>
                Add Resource
              </h3>
              <button
                onClick={() => {
                  setShowAddResourceModal(false);
                  setNewResourceTitle('');
                  setNewResourceDescription('');
                  setNewResourceUrl('');
                  setNewResourceContent('');
                  setNewResourceType('URL');
                }}
                className={`p-1 rounded transition-colors ${
                  isSuppleMode 
                    ? 'supple-text-muted hover:supple-text-primary' 
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Resource Type */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isSuppleMode ? 'supple-text-primary' : 'text-gray-700'
                }`}>
                  Type
                </label>
                <div className="flex gap-2">
                  {['URL', 'NOTE'].map((type) => (
                    <button
                      key={type}
                      onClick={() => setNewResourceType(type)}
                      className={`px-3 py-1 text-sm rounded transition-colors ${
                        newResourceType === type
                          ? isSuppleMode
                            ? 'supple-btn-gradient-blue text-white'
                            : 'bg-blue-600 text-white'
                          : isSuppleMode
                            ? 'supple-card-glass-hover supple-text-primary'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Title */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isSuppleMode ? 'supple-text-primary' : 'text-gray-700'
                }`}>
                  Title *
                </label>
                <input
                  type="text"
                  value={newResourceTitle}
                  onChange={(e) => setNewResourceTitle(e.target.value)}
                  className={`w-full px-3 py-2 rounded border transition-colors ${
                    isSuppleMode 
                      ? 'supple-card-glass supple-border-subtle supple-text-primary' 
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  }`}
                  placeholder="Enter resource title"
                />
              </div>
              
              {/* Description */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isSuppleMode ? 'supple-text-primary' : 'text-gray-700'
                }`}>
                  Description
                </label>
                <input
                  type="text"
                  value={newResourceDescription}
                  onChange={(e) => setNewResourceDescription(e.target.value)}
                  className={`w-full px-3 py-2 rounded border transition-colors ${
                    isSuppleMode 
                      ? 'supple-card-glass supple-border-subtle supple-text-primary' 
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  }`}
                  placeholder="Optional description"
                />
              </div>
              
              {/* URL Field */}
              {newResourceType === 'URL' && (
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isSuppleMode ? 'supple-text-primary' : 'text-gray-700'
                  }`}>
                    URL *
                  </label>
                  <input
                    type="url"
                    value={newResourceUrl}
                    onChange={(e) => setNewResourceUrl(e.target.value)}
                    className={`w-full px-3 py-2 rounded border transition-colors ${
                      isSuppleMode 
                        ? 'supple-card-glass supple-border-subtle supple-text-primary' 
                        : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    placeholder="https://example.com"
                  />
                </div>
              )}
              
              {/* Content Field */}
              {newResourceType === 'NOTE' && (
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isSuppleMode ? 'supple-text-primary' : 'text-gray-700'
                  }`}>
                    Content *
                  </label>
                  <textarea
                    value={newResourceContent}
                    onChange={(e) => setNewResourceContent(e.target.value)}
                    rows={4}
                    className={`w-full px-3 py-2 rounded border transition-colors ${
                      isSuppleMode 
                        ? 'supple-card-glass supple-border-subtle supple-text-primary' 
                        : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    placeholder="Enter note content"
                  />
                </div>
              )}
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddResourceModal(false);
                  setNewResourceTitle('');
                  setNewResourceDescription('');
                  setNewResourceUrl('');
                  setNewResourceContent('');
                  setNewResourceType('URL');
                }}
                className={`px-4 py-2 rounded transition-colors ${
                  isSuppleMode 
                    ? 'supple-text-muted hover:supple-text-primary hover:supple-card-glass-hover' 
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleAddResource}
                disabled={addingResource || !newResourceTitle.trim() || 
                  (newResourceType === 'URL' && !newResourceUrl.trim()) ||
                  (newResourceType === 'NOTE' && !newResourceContent.trim())
                }
                className={`px-4 py-2 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isSuppleMode 
                    ? 'supple-btn-gradient-blue text-white hover:supple-btn-gradient-blue-hover' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {addingResource ? 'Adding...' : 'Add Resource'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      </div>
    </>
  );
};

export default Sidebar;